import {
  AsyncMultiSelectField,
  AsyncSingleSelectField,
  CharField,
  MultiSelectField,
  NumberField,
  SingleSelectField,
  SwitchField,
} from "@inmagik/magik-ui"
import { getIn, useFormikContext } from "formik"
import { Col, Row } from "react-bootstrap"
import { usePersonaCreate, usePersonaLazy, usePersoneLazy } from "../../hooks/persone"
import { Edizione } from "../../types"
import { useOperaCreate, useOperaLazy, useOpereLazy } from "../../hooks/opere"
import { useCittaCreate, useCittaLazy, useCittaMultiLazy } from "../../hooks/citta"
import useModalTrigger from "magik-react-hooks/useModalTrigger"
import OperaModal from "../Opere/OperaModal"
import CittaModal from "../Citta/CittaModal"
import PersonaModal from "../Persone/PersonaModal"
import { useFormatiLazy, useFormatoCreate, useFormatoLazy } from "../../hooks/formati"
import FormatoModal from "../Formati/FormatoModal"
import { transformErrorForForm } from "../../utils/utils"
import { WysiwygField } from "../../components/WysiwygField"

export default function EdizioneForm({ col = 8, showOpera = true }: { col?: number; showOpera?: boolean }) {
  const { setFieldValue, values, errors, submitCount } = useFormikContext<Edizione>()

  const [modalStateCreateOpera, modalActionsCreateOpera] = useModalTrigger()
  const [modalStateCreateCitta, modalActionsCreateCitta] = useModalTrigger()
  const [modalStateCreatePersona, modalActionsCreatePersona] = useModalTrigger()
  const [modalStateCreateFormato, modalActionsCreateFormato] = useModalTrigger()

  const createOpera = useOperaCreate()
  const createCitta = useCittaCreate()
  const createPersona = usePersonaCreate()
  const createFormato = useFormatoCreate()

  const fetchOpera = useOperaLazy()
  const fetchOpere = useOpereLazy()

  const fetchCitta = useCittaLazy()
  const fetchCittaMulti = useCittaMultiLazy()

  const fetchPersona = usePersonaLazy()
  const fetchPersone = usePersoneLazy()

  const fetchFormato = useFormatoLazy()
  const fetchFormati = useFormatiLazy()

  return (
    <Row className="align-items-center mb-4">
      <Col xs={col} className="d-flex flex-column align-items-stretch gap-4">
        {showOpera && (
          <AsyncSingleSelectField
            label="Opera"
            layout="horizontal"
            placeholder="Seleziona un'opera"
            onChange={(value) => {
              setFieldValue("opera", value)
            }}
            clearable
            value={values.opera}
            loadOptions={(inputValue: string) => {
              return fetchOpere({ search: inputValue }).then((opere) => {
                return opere.results.map((opera) => ({
                  label: opera.titolo,
                  value: opera.id,
                }))
              })
            }}
            loadInitialValue={(id: number) =>
              fetchOpera(id).then((opera) => {
                return { label: opera.titolo, value: opera.id }
              })
            }
            required
            isValid={submitCount > 0 ? !getIn(errors, "opera") : undefined}
            errorMessage={getIn(errors, "opera")}
          >
            <SingleSelectField.Add
              onClick={() => {
                modalActionsCreateOpera.open()
              }}
            />
          </AsyncSingleSelectField>
        )}
        <CharField
          label="Titolo edizione"
          layout="horizontal"
          value={values.titolo}
          onChange={(value) => {
            setFieldValue("titolo", value)
          }}
        />
        <AsyncSingleSelectField
          label="Città"
          placeholder="Seleziona una città"
          layout="horizontal"
          onChange={(value) => {
            setFieldValue("citta", value)
          }}
          clearable
          value={values.citta}
          loadOptions={(inputValue: string) => {
            return fetchCittaMulti({ search: inputValue }).then((citta) => {
              return citta.results.map((citta) => ({
                label: citta.nome,
                value: citta.id,
              }))
            })
          }}
          loadInitialValue={(id: number) =>
            fetchCitta(id).then((citta) => {
              return { label: citta.nome, value: citta.id }
            })
          }
          isValid={submitCount > 0 ? !getIn(errors, "citta") : undefined}
          errorMessage={getIn(errors, "citta")}
          required
        >
          <SingleSelectField.Add
            onClick={() => {
              modalActionsCreateCitta.open()
            }}
          />
        </AsyncSingleSelectField>
        <NumberField
          label="Anno"
          layout="horizontal"
          value={values.anno}
          onChange={(value) => {
            setFieldValue("anno", value)
          }}
          isValid={submitCount > 0 ? !getIn(errors, "anno") : undefined}
          errorMessage={getIn(errors, "anno")}
          required
        />
        <AsyncMultiSelectField
          label="Committenti"
          layout="horizontal"
          placeholder="Seleziona uno o più committenti"
          onChange={(value) => {
            setFieldValue("committenti", value)
          }}
          clearable
          value={values.committenti || []}
          loadInitialValues={(ids: number[]) =>
            Promise.all(
              ids?.map((id) => fetchPersona(id).then((persona) => ({ label: persona.nome, value: persona.id })))
            )
          }
          loadOptions={(inputValue: string) => {
            return fetchPersone({ search: inputValue }).then((persone) => {
              return persone.results?.map((persona) => ({
                label: persona.nome,
                value: persona.id,
              }))
            })
          }}
        >
          <AsyncMultiSelectField.Add
            onClick={() => {
              modalActionsCreatePersona.open("committenti")
            }}
          />
        </AsyncMultiSelectField>
        
        <AsyncMultiSelectField
          label="Autori secondari"
          layout="horizontal"
          placeholder="Seleziona uno o più autori secondari"
          onChange={(value) => {
            setFieldValue("autori_secondari", value)
          }}
          clearable
          value={values.autori_secondari || []}
          loadInitialValues={(ids: number[]) =>
            Promise.all(
              ids?.map((id) => fetchPersona(id).then((persona) => ({ label: persona.nome, value: persona.id })))
            )
          }
          loadOptions={(inputValue: string) => {
            return fetchPersone({ search: inputValue }).then((persone) => {
              return persone.results?.map((persona) => ({
                label: persona.nome,
                value: persona.id,
              }))
            })
          }}
        >
          <AsyncMultiSelectField.Add
            onClick={() => {
              modalActionsCreatePersona.open("autori_secondari")
            }}
          />
        </AsyncMultiSelectField>
        <AsyncMultiSelectField
          label="Stampatori"
          layout="horizontal"
          placeholder="Seleziona uno o più stampatori"
          onChange={(value) => {
            setFieldValue("stampatori", value)
          }}
          clearable
          value={values.stampatori}
          loadInitialValues={(ids: number[]) =>
            Promise.all(
              ids.map((id) => fetchPersona(id).then((persona) => ({ label: persona.nome, value: persona.id })))
            )
          }
          loadOptions={(inputValue: string) => {
            return fetchPersone({ search: inputValue }).then((persone) => {
              return persone.results.map((persona) => ({
                label: persona.nome,
                value: persona.id,
              }))
            })
          }}
        >
          <MultiSelectField.Add
            onClick={() => {
              modalActionsCreatePersona.open("stampatori")
            }}
          />
        </AsyncMultiSelectField>
        <AsyncSingleSelectField
          label="Formato"
          layout="horizontal"
          placeholder="Seleziona un formato"
          onChange={(value) => {
            setFieldValue("formato", value)
          }}
          clearable
          value={values.formato}
          loadOptions={(inputValue: string) => {
            return fetchFormati({ search: inputValue }).then((formati) => {
              return formati.results.map((formato) => ({
                label: formato.nome,
                value: formato.id,
              }))
            })
          }}
          loadInitialValue={(id: number) =>
            fetchFormato(id).then((formato) => {
              return { label: formato.nome, value: formato.id }
            })
          }
          isValid={submitCount > 0 ? !getIn(errors, "formato") : undefined}
          errorMessage={getIn(errors, "formato")}
          required
        >
          <SingleSelectField.Add
            onClick={() => {
              modalActionsCreateFormato.open()
            }}
          />
        </AsyncSingleSelectField>
        <WysiwygField
          label="Repertori"
          layout="horizontal"
          value={values.repertori}
          onChange={(value) => {
            setFieldValue("repertori", value)
          }}
        />
        <CharField
          label="Collazione"
          layout="horizontal"
          value={values.collazione}
          onChange={(value) => {
            setFieldValue("collazione", value)
          }}
        />
        <WysiwygField
          label="Incisioni"
          layout="horizontal"
          value={values.incisioni}
          onChange={(value) => {
            setFieldValue("incisioni", value)
          }}
          rows={4}
        />
        <WysiwygField
          label="Note"
          layout="horizontal"
          value={values.note}
          onChange={(value) => {
            setFieldValue("note", value)
          }}
          rows={6}
        />
        <SwitchField
          label="Bozza"
          layout="horizontal"
          value={values.bozza}
          onChange={(value) => {
            setFieldValue("bozza", value)
          }}
        />
      </Col>
      {modalStateCreateOpera.isOpen && (
        <OperaModal
          show={modalStateCreateOpera.isOpen}
          onClose={modalActionsCreateOpera.close}
          save={(values) => {
            return createOpera.mutateAsync(values).then((opera) => {
              setFieldValue("opera", opera.id)
              modalActionsCreateOpera.close()
            })
          }}
          title="Nuova opera"
        />
      )}
      {modalStateCreateCitta.isOpen && (
        <CittaModal
          show={modalStateCreateCitta.isOpen}
          onClose={modalActionsCreateCitta.close}
          save={(values) => {
            return createCitta.mutateAsync(values).then((citta) => {
              setFieldValue("citta", citta.id)
              modalActionsCreateCitta.close()
            })
          }}
          title="Nuova città"
        />
      )}
      {modalStateCreatePersona.isOpen && (
        <PersonaModal
          show={modalStateCreatePersona.isOpen}
          onClose={modalActionsCreatePersona.close}
          save={(valuesModal, actions) => {
            return createPersona.mutateAsync(valuesModal).then((persona) => {
              setFieldValue(
                modalStateCreatePersona.value,
                modalStateCreatePersona.value === "committenti" ? [...(values.committenti || []), persona.id] : modalStateCreatePersona.value === "autori_secondari"
                  ? [...(values.autori_secondari || []), persona.id]
                  : [...(values.stampatori || []), persona.id]
              ).catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
              modalActionsCreatePersona.close()
            })
          }}
          title={`Nuovo ${modalStateCreatePersona.value}`}
        />
      )}
      {modalStateCreateFormato.isOpen && (
        <FormatoModal
          show={modalStateCreateFormato.isOpen}
          onClose={modalActionsCreateFormato.close}
          save={(values, actions) => {
            return createFormato
              .mutateAsync(values)
              .then((formato) => {
                setFieldValue("formato", formato.id)
                modalActionsCreateFormato.close()
              })
              .catch((e) => {
                actions.setErrors(transformErrorForForm(e))
              })
          }}
          title="Nuovo formato"
        />
      )}
    </Row>
  )
}
